"use client";

// The customer-photo grid inside the proof wall.
//
// Density is the point. A print bought on a watermarked render is a leap of faith, and the one
// thing that answers it is other people's prints on other people's walls — so the wall shows eight
// at once instead of three at a time. Every tile carries the car it shows: a stranger's photo is
// evidence, a stranger's photo of a car like yours is proof (Cialdini's similarity).
//
// The curated set is three times the grid, so the wall rotates it: every few seconds one tile
// crossfades to a photo that is not on screen, which leaves each photo up for half a minute. The
// wall holds still while it is off screen, under the pointer or keyboard focus, or open in the
// review viewer, so nothing ever changes under a hand that is about to click it.

import { useEffect, useRef, useState } from "react";
import CdnImage from "@/components/CdnImage";
import { Stars } from "@/components/ui/Stars";
import type { ReviewPhotoModalLabels, ReviewPhotoSlide } from "@/components/ReviewPhotoModal";
import { useIdleModule } from "@/lib/idlePreload";

// The review modal is closed on arrival and the wall is below the fold: its chunk is fetched once
// the page is idle after load (or when a finger lands on a tile), not with the page.
const loadModal = () => import("@/components/ReviewPhotoModal");
const preloadModal = () => {
  void loadModal();
};

export type ProofPhoto = ReviewPhotoSlide;

// How much of the review a tile carries. Long enough for a whole short opening sentence, short
// enough that it never crowds the photo it is supposed to be evidence about.
const OPENER_MAX = 80;

const TILES = 8;
// One tile changes every 3 to 5 seconds, so each of the eight stays up about 30 seconds.
const SWAP_MIN_MS = 3000;
const SWAP_MAX_MS = 5000;
const FADE_MS = 1000;
// The order tiles first change in: scattered over the grid, so the first swap is not always the
// top-left corner. After that the tile that has been up longest goes next.
const FIRST_SWAPS = [5, 2, 7, 0, 3, 6, 1, 4];

/** Which photo each tile shows, which photos wait off screen (next first), and the tiles from
 *  longest shown to most recently changed. All indexes into `photos`. */
type Rotation = { tiles: number[]; waiting: number[]; age: number[] };

function initialRotation(count: number): Rotation {
  const shown = Math.min(count, TILES);
  return {
    tiles: Array.from({ length: shown }, (_, i) => i),
    waiting: Array.from({ length: count - shown }, (_, i) => shown + i),
    age: FIRST_SWAPS.filter((tile) => tile < shown),
  };
}

/** Swaps one of the three longest-shown tiles for the photo that has waited longest. */
function rotate(rotation: Rotation, random: number): Rotation {
  const tile = rotation.age[Math.floor(random * Math.min(3, rotation.age.length))];
  const [next, ...waiting] = rotation.waiting;
  return {
    tiles: rotation.tiles.map((shown, t) => (t === tile ? next : shown)),
    waiting: [...waiting, rotation.tiles[tile]],
    age: [...rotation.age.filter((t) => t !== tile), tile],
  };
}

/** The review's first sentence, cut on a word boundary when it runs past the tile. */
function opener(text?: string | null): string | null {
  const trimmed = text?.trim();
  if (!trimmed) return null;
  const stop = trimmed.search(/[.!?](\s|$)/);
  const sentence = stop > 0 ? trimmed.slice(0, stop + 1) : trimmed;
  if (sentence.length <= OPENER_MAX) return sentence;
  const cut = sentence.slice(0, OPENER_MAX);
  const lastSpace = cut.lastIndexOf(" ");
  return `${lastSpace > OPENER_MAX / 2 ? cut.slice(0, lastSpace) : cut}…`;
}

export function ProofPhotoWall({
  photos,
  labels,
  locale,
}: {
  photos: ProofPhoto[];
  labels: ReviewPhotoModalLabels & { enlarge: string };
  locale: string;
}) {
  const [index, setIndex] = useState<number | null>(null);
  const [rotation, setRotation] = useState(() => initialRotation(photos.length));
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const ReviewPhotoModal = useIdleModule(loadModal, index !== null)?.ReviewPhotoModal;
  const paused = index !== null || hovered || focused || !visible;

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.3 });
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !rotation.waiting.length) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const delay = SWAP_MIN_MS + Math.random() * (SWAP_MAX_MS - SWAP_MIN_MS);
    const timer = window.setTimeout(() => setRotation((current) => rotate(current, Math.random())), delay);
    return () => window.clearTimeout(timer);
  }, [paused, rotation]);

  if (!photos.length) return null;

  return (
    <>
      <ul
        className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-4"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
        }}
        // Keyboard focus only: a clicked tile keeps focus after the viewer closes, and that should
        // not freeze the wall.
        onFocus={(event) => setFocused(event.target.matches(":focus-visible"))}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        ref={listRef}
      >
        {rotation.tiles.map((photoIndex, tile) => (
          <li key={tile}>
            <ProofTile
              enlargeLabel={labels.enlarge}
              onOpen={(url) => setIndex(photos.findIndex((photo) => photo.url === url))}
              photo={photos[photoIndex]}
            />
          </li>
        ))}
      </ul>

      {index === null || !ReviewPhotoModal ? null : (
        /* Not a bare lightbox: the photo opens with the review that produced it, and the arrows
           walk the whole curated set, not just the eight on screen. */
        <ReviewPhotoModal
          index={index}
          labels={labels}
          locale={locale}
          onClose={() => setIndex(null)}
          onIndexChange={setIndex}
          slides={photos}
        />
      )}
    </>
  );
}

/** One tile. A new photo loads invisibly on top of the current one and fades in once it has
 *  loaded, so a tile never fades through an empty frame; until then a click opens what is shown. */
function ProofTile({
  photo,
  enlargeLabel,
  onOpen,
}: {
  photo: ProofPhoto;
  enlargeLabel: string;
  onOpen: (url: string) => void;
}) {
  const [shown, setShown] = useState(photo);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const incoming = photo.url === shown.url ? null : photo;
  const fading = incoming !== null && loadedUrl === incoming.url;
  const visible = fading ? incoming : shown;

  useEffect(() => {
    if (!fading) return;
    const timer = window.setTimeout(() => setShown(incoming), FADE_MS);
    return () => window.clearTimeout(timer);
  }, [fading, incoming]);

  return (
    <button
      aria-label={enlargeLabel.replace("{name}", visible.car)}
      className="group relative block aspect-[4/5] w-full overflow-hidden border border-rule bg-paper-deep"
      data-testid="proof-photo-open"
      onClick={() => onOpen(visible.url)}
      onPointerDown={preloadModal}
      type="button"
    >
      {/* Keyed by photo, so the faded-in layer keeps its <img> when it becomes the shown one. */}
      {[shown, incoming].map((layer) =>
        layer ? (
          <span
            className={`absolute inset-0 transition-opacity ease-in-out ${layer === incoming && !fading ? "opacity-0" : "opacity-100"}`}
            key={layer.url}
            style={{ transitionDuration: `${FADE_MS}ms` }}
          >
            <CdnImage
              alt={layer.car}
              className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              defer="near"
              fill
              onLoad={layer === incoming ? () => setLoadedUrl(layer.url) : undefined}
              sizes="(min-width: 768px) 25vw, 50vw"
              src={layer.url}
            />
            {/* The caption is the evidence, so it stays on the tile rather than under it: the
                photo and the car it shows have to be read as one thing. The stars and the
                opening line come with it — a tile that shows the print and says what the buyer
                thought of it is a review, not a thumbnail. */}
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 via-ink/70 to-transparent px-2 pb-1.5 pt-7 text-left">
              <Stars className="h-2.5 w-2.5" value={layer.rating} />
              <span className="mt-1 block truncate font-mono text-[9px] uppercase tracking-[0.06em] text-paper">
                {layer.car}
              </span>
              {opener(layer.reviewText) ? (
                <span className="mt-0.5 block font-sans text-[10px] leading-[1.35] text-paper/75 line-clamp-2">
                  &ldquo;{opener(layer.reviewText)}&rdquo;
                </span>
              ) : null}
            </span>
          </span>
        ) : null,
      )}
    </button>
  );
}
